import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { ratingsFilterToSet } from '@/modules/analytics/utils/expandRatings';
import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { Log, LogsQueryVariables } from '@/shared/types/analytics';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { mapFirstMessageTypeFilter } from '@/util/firstMessageTypeFilter';
import { parseFilter, isUuid } from '@/util/util';

import { LogSessionsCount, LogSessionsPreview, SESSIONS_COUNT, SESSIONS_PREVIEW } from './useLogs';

export interface AccountLogsInfiniteQueryResult {
  data: Log[];
  isLoading: boolean;
  isError: boolean;
  error?: ErrorLike;
  fetchMore: () => Promise<void>;
  totalCount: number;
  isCountLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

interface UseAccountLogsInfiniteQueryOptions {
  limit?: number;
}

/**
 * Hook for fetching account-level chat logs with infinite scrolling support.
 * Mirrors the legacy `useChatlogs` variable mapping while leveraging the new table UX.
 */
export function useAccountLogsInfiniteQuery({
  limit = 20,
}: UseAccountLogsInfiniteQueryOptions = {}): AccountLogsInfiniteQueryResult {
  const { t } = useTranslation();
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);
  const filters = useSelector(selectFilters);
  const { analytics, startDate, endDate, filtersLoaded } = filters;

  const variables = useMemo(() => {
    const baseVariables = { ...getAnalyticsRangeLocal(startDate, endDate), accountId };

    const analyticsFilters = (analytics ?? [])
      .filter((filter) => filterFiltersByUrl(filter?.type, '/account-chat-logs', t))
      .map((filter) => {
        if (filter.type === 'reactions') {
          return {
            min_num_thumbs_up: filter[filter.type] === true ? 1 : 0,
            min_num_thumbs_down: filter[filter.type] === false ? 1 : 0,
          };
        }

        if (filter.type === 'firstMessageType') {
          return mapFirstMessageTypeFilter(filter.firstMessageType);
        }

        if (filter.type === 'ratings') {
          // An older saved filter still stores a threshold, which has to expand.
          return { ratings: ratingsFilterToSet(filter.ratings, filter.ratingsMode) };
        }

        if (filter.type === 'tags') {
          return { tags: parseFilter(filter), tagsMatch: filter.tagsMode ?? 'any' };
        }

        return { [filter.type]: parseFilter(filter) };
      });

    return Object.assign({}, baseVariables, ...analyticsFilters);
  }, [accountId, analytics, startDate, endDate, t]);

  const skip = !isUuid(accountId) || !!brainId || !filtersLoaded;

  const variablesSignature = useMemo(
    () => JSON.stringify({ accountId, startDate, endDate, analytics }),
    [accountId, startDate, endDate, analytics]
  );

  const [hasMore, setHasMore] = useState(true);
  const inFlightOffsetRef = useRef<number | null>(null);

  useEffect(() => {
    setHasMore(true);
    inFlightOffsetRef.current = null;
  }, [variablesSignature]);

  const {
    data: previewData,
    loading,
    error,
    fetchMore: apolloFetchMore,
    networkStatus,
  } = useQuery<LogSessionsPreview, LogsQueryVariables>(SESSIONS_PREVIEW, {
    variables: { ...variables, limit, offset: 0 },
    skip,
    notifyOnNetworkStatusChange: true,
  });

  const data = previewData?.rows ?? [];

  const { data: countData, loading: isCountLoading } = useQuery<
    LogSessionsCount,
    LogsQueryVariables
  >(SESSIONS_COUNT, {
    variables,
    skip,
    // Count is non-critical — fall back to page-length heuristic on failure
    errorPolicy: 'ignore',
  });

  const totalCount = countData?.rows?.[0]?.total_count ?? 0;

  // Use aggregate count when available, otherwise assume more pages exist
  // as long as we received a full page of results and hasMore is true.
  const aggregateHasNext = totalCount > 0 && data.length < totalCount;
  const fullPageHasNext = data.length > 0 && data.length % limit === 0;
  const computedHasNextPage = totalCount > 0 ? aggregateHasNext : fullPageHasNext;
  const hasNextPage = hasMore && computedHasNextPage;
  const isFetchingNextPage = networkStatus === 3;

  const handleFetchMore = useCallback(async () => {
    if (!hasNextPage || loading || isFetchingNextPage) {
      return;
    }

    const nextOffset = data.length;

    if (inFlightOffsetRef.current === nextOffset) {
      return;
    }

    inFlightOffsetRef.current = nextOffset;

    try {
      let fetchedCount = 0;

      await apolloFetchMore({
        variables: { ...variables, limit, offset: nextOffset },
        updateQuery: (prev, { fetchMoreResult }) => {
          fetchedCount = fetchMoreResult?.rows?.length ?? 0;

          if (!fetchMoreResult || fetchedCount === 0) {
            return prev;
          }

          return { ...prev, rows: [...prev.rows, ...fetchMoreResult.rows] };
        },
      });

      if (fetchedCount === 0 || fetchedCount < limit) {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching more sessions:', err);
    } finally {
      inFlightOffsetRef.current = null;
    }
  }, [apolloFetchMore, variables, limit, data.length, hasNextPage, loading, isFetchingNextPage]);

  return {
    data,
    isLoading: loading && !data.length,
    isError: !!error,
    error,
    fetchMore: handleFetchMore,
    totalCount,
    isCountLoading,
    hasNextPage,
    isFetchingNextPage,
  };
}
